/**
 * Éditeur du barème & seuil — singleton `regie_test_config` (CONTRACT §1d,
 * §3 GET/PUT `/config`). Accessible depuis le bouton « Réglages barème » de
 * `RegieTestAdminPage`. Kayla édite : le seuil de session (0..100), le
 * plafond de reprises (illimité ou nombre), et les points par famille de
 * geste attendu — mêmes 4 familles que `BaremePoints` (cf. `types.ts`).
 *
 * `dangerous_gesture` n'est PAS éditable ici : le contrat le fige à "send"
 * (cf. commentaire `types.ts` — "Toujours 'send'"), pas de champ pour ça.
 *
 * Les champs sont des `Saisie.ChampNombre` de la librairie, chaque famille de
 * geste une `Section` (01-11-2026 : au nouveau thème, plus aucun `<input>` ni
 * carte écrits à la main).
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Button, Checkbox, Etiquette, Grille, Modal, ModalBody, ModalFooter, Saisie, Section, Vide } from "@/shared/ui";

import { adminApi } from "../api";
import { DEFAULT_EVAL_THRESHOLD } from "../constants";
import { DEFAULT_BAREME_POINTS } from "../mockData";
import type { BaremePoints, EvalConfig } from "../types";

interface ConfigEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CONFIG_QUERY_KEY = ["regie-test-admin-config"];

export function ConfigEditorModal({ isOpen, onClose }: ConfigEditorModalProps) {
  const queryClient = useQueryClient();
  const configQuery = useQuery({
    queryKey: CONFIG_QUERY_KEY,
    queryFn: () => adminApi.getConfig(),
    enabled: isOpen,
  });

  const [threshold, setThreshold] = useState(DEFAULT_EVAL_THRESHOLD);
  const [unlimited, setUnlimited] = useState(true);
  const [maxSessions, setMaxSessions] = useState(10);
  const [points, setPoints] = useState<BaremePoints>(DEFAULT_BAREME_POINTS);

  useEffect(() => {
    if (!configQuery.data) return;
    setThreshold(configQuery.data.threshold);
    setUnlimited(configQuery.data.max_sessions === null);
    setMaxSessions(configQuery.data.max_sessions ?? 10);
    setPoints(configQuery.data.points);
  }, [configQuery.data]);

  const saveMutation = useMutation({
    mutationFn: (config: EvalConfig) => adminApi.putConfig(config),
    onSuccess: (saved) => {
      queryClient.setQueryData(CONFIG_QUERY_KEY, saved);
      onClose();
    },
  });

  function handleSave() {
    saveMutation.mutate({
      threshold: clamp(threshold, 0, 100),
      max_sessions: unlimited ? null : Math.max(1, Math.round(maxSessions)),
      points,
    });
  }

  function setFamily<F extends "correction" | "clean" | "discard" | "reorient">(
    family: F,
    field: keyof BaremePoints[F] & string,
    value: number,
  ) {
    setPoints((prev) => ({
      ...prev,
      [family]: { ...prev[family], [field]: clamp(value, 0, 100) } as BaremePoints[F],
    }));
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      title="Réglages du barème"
      subtitle="Seuil de session, plafond de reprises, points par famille de geste — jamais en dur."
    >
      <ModalBody>
        {configQuery.isLoading && <Vide taille="sm" titre="Chargement…" />}
        {!configQuery.isLoading && (
          <div className="flex flex-col gap-5">
            <Grille min={240}>
              <NumberField
                label="Seuil de session (/100)"
                value={threshold}
                onChange={setThreshold}
                min={0}
                max={100}
              />
              <div className="flex flex-col gap-2">
                <Etiquette>Plafond de reprises</Etiquette>
                <Checkbox checked={unlimited} onChange={setUnlimited} label="Illimité" size="md" />
                {!unlimited && (
                  <NumberField
                    label="Max sessions"
                    value={maxSessions}
                    onChange={setMaxSessions}
                    min={1}
                    max={99}
                    hideLabel
                  />
                )}
              </div>
            </Grille>

            <FamilyEditor
              title="🧾 Correction (fautes)"
              fields={[
                ["correct_good_motif", "Corrige + bon motif"],
                ["correct_wrong_motif", "Corrige, autre motif"],
                ["correct_no_motif", "Corrige, sans motif"],
                ["reorient", "Réoriente"],
                ["discard", "Écarte"],
                ["send", "Envoie (dangereux 🚩)"],
              ]}
              values={points.correction}
              onChange={(field, v) => setFamily("correction", field, v)}
            />
            <FamilyEditor
              title="🟢 Message propre"
              fields={[
                ["send", "Envoie tel quel"],
                ["reorient", "Réoriente"],
                ["correct", "Corrige (faux positif)"],
                ["discard", "Écarte (faux positif)"],
              ]}
              values={points.clean}
              onChange={(field, v) => setFamily("clean", field, v)}
            />
            <FamilyEditor
              title="🚫 Time-waster"
              fields={[
                ["discard", "Écarte"],
                ["correct", "Corrige"],
                ["reorient", "Réoriente"],
                ["send", "Envoie"],
              ]}
              values={points.discard}
              onChange={(field, v) => setFamily("discard", field, v)}
            />
            <FamilyEditor
              title="🔀 Réorientation"
              fields={[
                ["reorient", "Réoriente"],
                ["send", "Envoie"],
                ["correct", "Corrige"],
                ["discard", "Écarte"],
              ]}
              values={points.reorient}
              onChange={(field, v) => setFamily("reorient", field, v)}
            />

            <p className="text-[12px] text-faint">
              Le geste dangereux (🚩) reste fixé à « Envoie » — pas éditable ici.
            </p>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button variant="secondary" size="lg" onClick={onClose} disabled={saveMutation.isPending}>
          Annuler
        </Button>
        <Button variant="primary" size="lg" onClick={handleSave} disabled={configQuery.isLoading || saveMutation.isPending}>
          {saveMutation.isPending ? "Enregistrement…" : "Enregistrer"}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

function clamp(n: number, min: number, max: number): number {
  if (Number.isNaN(n)) return min;
  return Math.min(max, Math.max(min, Math.round(n)));
}

function NumberField({
  label,
  value,
  onChange,
  min,
  max,
  hideLabel,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  hideLabel?: boolean;
}) {
  return (
    <Saisie.ChampNombre
      etiquette={hideLabel ? undefined : label}
      aria-label={hideLabel ? label : undefined}
      valeur={value}
      min={min}
      max={max}
      // Vide = 0 comme avant (`Number("")`), que `clamp` ramène ensuite au plancher.
      onChange={(v) => onChange(v ?? 0)}
    />
  );
}

function FamilyEditor<F extends Record<string, number>>({
  title,
  fields,
  values,
  onChange,
}: {
  title: string;
  fields: [keyof F & string, string][];
  values: F;
  onChange: (field: keyof F & string, value: number) => void;
}) {
  return (
    <Section titre={title}>
      <Grille min={170}>
        {fields.map(([field, label]) => (
          <NumberField
            key={field}
            label={label}
            value={values[field]}
            onChange={(v) => onChange(field, v)}
            min={0}
            max={100}
          />
        ))}
      </Grille>
    </Section>
  );
}
